import React, { useEffect } from 'react';
import useSWR from 'swr';
import api from '~/_base/constants/api';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Navigation from '../_base/components/navigation';
import UseInlineHelp from '~/_base/hooks/useInlineHelp';
import getGeolocation from '../_base/functions/getGeolocation';
import SiteAssistant from './modules/siteAssistant';
import LiveView from './modules/liveView';
import Spinner from '~/_base/components/spinner';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthenticatedRoutes } from './routes';
import RailsNotification from '~/_base/components/railsNotification';
import ProcoreWelcome from './modules/procoreWelcome';
import Centered from '~/_base/components/centered';
//@ts-ignore
import baseCache from '~/_base/cache/baseCache';
//@ts-ignore
import { useSessionManager } from '~/_base/functions/sessionManager';
import { identifyWithGainsight } from '~/_base/functions/gainsight';
/**
 * The primary container App injected into every rails view.
 * Renders the primary components of the site which should be visible across
 * every page (navigation, site assistant, etc.), and lazy-loads
 * react-router pages if a valid route matches.
 *
 * Older apps are injected separately from different bundles. In the future it
 * would be nice to have a single entry bundle.
 */

export default function AuthenticatedApp() {
  const isAndroid = baseCache.retrieveFromCache('android') != null;
  const sessionManager = useSessionManager(isAndroid);
  const { i18n } = useTranslation();
  useEffect(() => {
    const lang = document.documentElement.lang;
    i18n.changeLanguage(lang);
    moment.locale(lang);
  }, [i18n]);

  // (Web only) Ping the server every 5 minutes of activity to refresh the session
  sessionManager.reset();

  UseInlineHelp();
  const { data: currentContext } = useSWR(
    api.currentContext.address,
    api.currentContext.request
  );
  const { data: sidebarData } = useSWR(
    api.sidemenu.address,
    api.sidemenu.request
  );
  const { data: navigationData } = useSWR(
    api.topNavigation.address,
    api.topNavigation.request
  );

  useEffect(() => {
    if (currentContext && (window as any).zE != null) {
      (window as any).zE('webWidget', 'identify', {
        name: `${currentContext.current_user.name} (${currentContext.current_organisation.name})`,
        email: currentContext.current_user.email,
      });
    }
  }, [currentContext]);

  // Full Aptrinsic identify once context is available
  useEffect(() => {
    identifyWithGainsight(currentContext as any);
  }, [currentContext]);

  const liveViewEnabled = navigationData
    ?.filter(menu => menu.key === 'insights')
    .some(
      menu =>
        'items' in menu &&
        menu.items.some(menuItem => menuItem.uri === '/live_view')
    );

  const siteAssistantEnabled = navigationData
    ?.filter(menu => menu.key === 'insights')
    .some(
      menu =>
        'items' in menu &&
        menu.items.some(menuItem => menuItem.uri === '/site_assistant')
    );

  const isFormOrTemplateEditor = !checkFormOrTemplateEditor();

  useEffect(() => {
    getGeolocation();
  }, []);

  return (
    <React.Fragment>
      <RailsNotification />
      {currentContext?.current_user != null &&
        sidebarData != null &&
        !isFormOrTemplateEditor && (
          <div style={{ display: 'flex', flex: 1, width: '100%' }}>
            <Router>
              <Navigation sidebarData={sidebarData}>
                <React.Suspense
                  fallback={
                    <Centered>
                      <Spinner />
                    </Centered>
                  }
                >
                  <AuthenticatedRoutes currentContext={currentContext} />
                </React.Suspense>
              </Navigation>
            </Router>
          </div>
        )}
      {siteAssistantEnabled && (
        <SiteAssistant
          position={1}
          selectedProject={currentContext?.current_project}
        />
      )}
      {liveViewEnabled && (
        <LiveView
          position={2}
          selectedProject={currentContext?.current_project}
        />
      )}
      {shouldRenderProcoreWelcome() && <ProcoreWelcome />}
    </React.Fragment>
  );
}
function shouldRenderProcoreWelcome() {
  return document.getElementsByClassName('procore-welcome')[0] != null;
}

function checkFormOrTemplateEditor() {
  const isTemplateEditor = document.getElementById('template_editor') != null;
  const isFormEditor = document.getElementsByTagName('form-editor').length > 0;
  if (isTemplateEditor || isFormEditor) {
    return false;
  }
  return true;
}
